import booleanEqual from "@turf/boolean-equal";
import { Feature, GeoJsonProperties, Geometry, Point, Position } from "geojson";
import { RBush } from "geojson-rbush";

import { feature as toFeature } from "fond/turf";
import { LayerIds } from "fond/types";

const EPSILON = 0.0000001;

/**
 * Determines if a Feature<Point> is collocated with a feature in the specified source.
 * For example, this can be used to check if a LineStrings vertex is collocated with a demand point.
 * @param point The point feature to check.
 * @param map The map instance.
 * @param sourceId The source ID to check against.
 * @returns True if the point is collocated to a feature in the source, false otherwise.
 */
export const isConnected = (point: Feature<Point>, map: mapboxgl.Map, source: string | { sourceId: string; sourceLayerId: string }): boolean => {
  if (typeof source === "string") {
    return map?.querySourceFeatures(source).some((f) => booleanEqual(f.geometry, point.geometry, { precision: 7 }));
  }
  return map
    ?.querySourceFeatures(source.sourceId, { sourceLayer: source.sourceLayerId })
    .some((f) => booleanEqual(f.geometry, point.geometry, { precision: 7 }));
};

/**
 * Determines if a Leadin Feature<LineString> is valid by checking that its vertices  are collocated with both a Pit & Demand.
 * @param feature The LineString feature to check.
 * @param map The map instance.
 * @returns True if the feature is valid, false otherwise.
 */
export const isValid = (
  feature: GeoJSON.Feature<GeoJSON.LineString>,
  map: mapboxgl.Map,
  opts: {
    demands: string | { sourceId: string; sourceLayerId: string };
    ugPoints: string | { sourceId: string; sourceLayerId: string };
  } = {
    demands: LayerIds.inAddress,
    ugPoints: LayerIds.inStreetPoints,
  }
): boolean => {
  const points = feature.geometry.coordinates.map((coord) => toFeature({ type: "Point", coordinates: coord }));
  const hasUGPoint = points.some((point) => isConnected(point, map, opts.ugPoints));
  const hasDemand = points.some((point) => isConnected(point, map, opts.demands));

  return hasUGPoint && hasDemand;
};

/**
 * Determines if a Leadin Feature<LineString> is valid by checking that its vertices are collocated with both a Pit & Demand.
 * @param feature The LineString feature to check.
 * @param tree The spatial index tree containing demand points and UG points.
 * @returns True if the feature is valid, false otherwise.
 */
export const isValidTree = (
  feature: GeoJSON.Feature<GeoJSON.LineString>,
  tree: RBush<Geometry, GeoJsonProperties>,
  opts: {
    demands: { sourceId: string; sourceLayerId?: string };
    ugPoints: { sourceId: string; sourceLayerId?: string };
  } = {
    demands: { sourceId: LayerIds.inAddress, sourceLayerId: undefined },
    ugPoints: { sourceId: LayerIds.inStreetPoints, sourceLayerId: undefined },
  }
): boolean => {
  // We valid each lead-in based on whether it is connected to both a demand point & an underground point.
  const nearbyToPointOne = getNearbyFeatures(feature.geometry.coordinates[0], tree);
  const nearbyToPointTwo = getNearbyFeatures(feature.geometry.coordinates[1], tree);

  const connectedFeatures = [...nearbyToPointOne, ...nearbyToPointTwo];
  const hasDemand = connectedFeatures.some(
    (f) => f.properties?.layerId === (typeof opts.demands === "string" ? opts.demands : opts.demands.sourceLayerId)
  );
  const hasUgPoints = connectedFeatures.some(
    (f) => f.properties?.layerId === (typeof opts.ugPoints === "string" ? opts.ugPoints : opts.ugPoints.sourceLayerId)
  );

  return hasDemand && hasUgPoints;
};

// Helper function to get nearby features from spatial index, an EPSILON buffer is used
// to account for floating point precision issues.
const getNearbyFeatures = (coordinates: Position, tree: RBush<Geometry, GeoJsonProperties>) => {
  return tree.search([coordinates[0] - EPSILON, coordinates[1] - EPSILON, coordinates[0] + EPSILON, coordinates[1] + EPSILON]).features;
};
